import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { LanguageToggle } from '../components/LanguageToggle';
import { ThemeModeToggle } from '../components/ThemeModeToggle';
import { useT } from '../i18n/LanguageContext';

import type { NavigationGroup, NavigationItem } from './NavigationItem';

const DRAWER_WIDTH = 248;

type AppLayoutProps = {
  navigation: Array<NavigationGroup>;
  /** Who is signed in, as two lines: a name and what they are. */
  identity?: { primary: string; secondary: string };
  /** Shown under the brand — the tenant name on the business side. */
  context?: ReactNode;
  onSignOut?: () => void;
};

/**
 * The shell every signed-in screen renders inside. Below `md` the sidebar becomes a drawer you open: a
 * permanent rail on a phone leaves a table no room at all.
 */
export const AppLayout = ({ navigation, identity, context, onSignOut }: AppLayoutProps) => {
  const t = useT();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const groups: Array<NavigationGroup> = navigation
    .map((group) => ({ heading: group.heading, items: group.items.filter((item) => item.visible) }))
    .filter((group) => group.items.length > 0);

  const isSelected = (item: NavigationItem): boolean =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1.5, minHeight: { xs: 58, sm: 58 } }}>
          {isNarrow ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t('layout.openMenu')}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="h6" sx={{ letterSpacing: '-0.015em' }}>
            {t('app.name')}
          </Typography>
          <Chip size="small" label={t('app.badge')} variant="outlined" sx={{ ml: 0.5 }} />
          {context}

          <Box sx={{ flexGrow: 1 }} />

          <LanguageToggle />
          <ThemeModeToggle />

          {identity ? (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />
              <Stack sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" sx={{ lineHeight: 1.25, fontWeight: 560 }}>
                  {identity.primary}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                  {identity.secondary}
                </Typography>
              </Stack>
            </>
          ) : null}
          {onSignOut ? (
            <Tooltip title={t('layout.signOut')}>
              <IconButton size="small" aria-label={t('layout.signOut')} onClick={onSignOut}>
                <LogoutOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isNarrow ? 'temporary' : 'permanent'}
        open={isNarrow ? isDrawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isNarrow ? 0 : DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 58, sm: 58 } }} />
        <Box sx={{ px: 1.5, py: 2, overflowY: 'auto' }}>
          {groups.map((group, groupIndex) => (
            <Box key={group.heading ?? `group-${groupIndex}`} sx={{ mb: 1.5 }}>
              {group.heading ? (
                <Typography
                  variant="overline"
                  color="text.disabled"
                  sx={{ px: 1.5, display: 'block', mb: 0.25 }}
                >
                  {t(group.heading)}
                </Typography>
              ) : null}
              <List disablePadding>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const selected = isSelected(item);

                  return (
                    <ListItemButton
                      key={item.path}
                      selected={selected}
                      onClick={() => {
                        navigate(item.path);
                        setDrawerOpen(false);
                      }}
                      sx={{ mb: 0.25 }}
                    >
                      <ListItemIcon sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.label)}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: selected ? 620 : 500,
                          color: selected ? 'text.primary' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', minWidth: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 58, sm: 58 } }} />
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};
